import React, { useState } from 'react';
import { Alert, Badge, Button, Col, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  usePostWebFormsSubmissionsSubmissionIdResend,
  usePatchWebFormsSubmissionsSubmissionId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { WebFormSubmission } from '../api/generated/model';
import { statusBadge, deliveryBadge } from './FormSubmissionsTable';

interface Props {
  submission: WebFormSubmission;
  onClose: () => void;
  onUpdated?: () => void;
}

const formatDateTime = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const FormSubmissionDetailModal: React.FC<Props> = ({ submission, onClose, onUpdated }) => {
  const resend = usePostWebFormsSubmissionsSubmissionIdResend();
  const reclassify = usePatchWebFormsSubmissionsSubmissionId();
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);
  const [sending, setSending] = useState(false);

  // The prop is a snapshot taken when the row was clicked, and refetching the
  // table behind us does not update it. Both mutations return the updated
  // submission, so hold it locally and render from that — otherwise the status
  // chip and the button keep describing the state before the click.
  // Safe to seed from the prop: the parent keys this modal by submission id,
  // so opening a different row remounts rather than reuses.
  const [current, setCurrent] = useState<WebFormSubmission>(submission);

  const fields = current.fields || {};
  const fieldKeys = Object.keys(fields).sort();
  const quarantined = current.status !== 'accepted';

  // Spam / not spam is the operator-facing classification. Marking not-spam
  // also delivers when nothing was sent, since a submission held back by a
  // failed verification has no notification email.
  const handleReclassify = async () => {
    setSending(true);
    setMsg(null);
    try {
      const updated = await reclassify.mutateAsync({
        submissionId: current.id,
        data: { status: quarantined ? 'accepted' : 'spam' },
      });
      setCurrent(updated);
      setMsg({
        variant: 'success',
        text: quarantined ? 'Marked as not spam.' : 'Marked as spam.',
      });
      onUpdated?.();
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to update',
      });
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    setSending(true);
    setMsg(null);
    try {
      const updated = await resend.mutateAsync({ submissionId: current.id });
      setCurrent(updated);
      setMsg({ variant: 'success', text: 'Delivery re-queued.' });
      onUpdated?.();
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to re-queue delivery',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span>{current.label || current.form_id}</span>
            {/* Whether this was withheld is the first thing anyone opening a
                submission needs to know, so it belongs in the header rather
                than only in the body copy below. */}
            <Badge bg={statusBadge(current.status).variant}>
              {statusBadge(current.status).label}
            </Badge>
            {quarantined && <Badge bg="dark">not delivered</Badge>}
          </div>
          <div className="small text-muted fw-normal">{formatDateTime(current.created_at)}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {msg && <Alert variant={msg.variant} dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

        {current.status === 'config_error' && (
          <Alert variant="danger">
            <strong>Verification could not be performed.</strong> This is not bot traffic —
            it is most likely a real enquiry that arrived while the Turnstile
            configuration was broken. Review it and resend if genuine.
          </Alert>
        )}
        {current.status === 'rejected' && (
          <Alert variant="warning">
            <strong>Failed Turnstile verification.</strong> Usually a bot, but if the
            keys had drifted this may be a real enquiry.
          </Alert>
        )}
        {current.status === 'spam' && (
          <Alert variant="secondary">
            Tripped the honeypot. The visitor received a normal success response so
            the bot learned nothing.
          </Alert>
        )}

        <h6 className="text-muted">Submitted fields</h6>
        <Table size="sm" bordered className="mb-4">
          <tbody>
            {fieldKeys.length === 0 && (
              <tr><td className="text-muted">No fields recorded.</td></tr>
            )}
            {fieldKeys.map(k => (
              <tr key={k}>
                <th style={{ width: '32%' }} className="text-nowrap">{k}</th>
                {/* Rendered as text, never as markup: this is
                    attacker-controlled content. */}
                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{fields[k]}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h6 className="text-muted">Delivery</h6>
        <Row className="mb-4">
          <Col md={6}>
            <div className="mb-2">
              <span className="text-muted me-2">Status:</span>
              <Badge bg={deliveryBadge(current.delivery_status).variant}>
                {deliveryBadge(current.delivery_status).label}
              </Badge>
            </div>
            <div className="mb-2"><span className="text-muted me-2">Sent at:</span>{formatDateTime(current.delivery_at) || '—'}</div>
            <div className="mb-2"><span className="text-muted me-2">Attempts:</span>{current.delivery_tries ?? 0}</div>
          </Col>
          <Col md={6}>
            {current.delivery_error && (
              <div className="mb-2">
                <div className="text-muted">Error</div>
                <div className="small text-danger" style={{ wordBreak: 'break-word' }}>{current.delivery_error}</div>
              </div>
            )}
          </Col>
        </Row>

        <h6 className="text-muted">Provenance</h6>
        <Row>
          <Col md={6}>
            <div className="mb-2"><span className="text-muted me-2">Form ID:</span>{current.form_id}</div>
            <div className="mb-2"><span className="text-muted me-2">Page:</span>{current.page_url || '—'}</div>
            <div className="mb-2">
              <span className="text-muted me-2">Client IP:</span>
              {current.client_ip || '—'}
              {current.client_ip && !current.client_ip_trusted && (
                // Worth surfacing: an untrusted address means per-IP rate
                // limiting degraded to global for this request.
                <Badge bg="secondary" className="ms-2">socket peer, not forwarded</Badge>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-2"><span className="text-muted me-2">Turnstile hostname:</span>{current.turnstile_hostname || '—'}</div>
            {current.reject_reason && (
              <div className="mb-2">
                <div className="text-muted">Reject reason</div>
                <div className="small" style={{ wordBreak: 'break-word' }}>{current.reject_reason}</div>
              </div>
            )}
            <div className="mb-2 small text-muted" style={{ wordBreak: 'break-word' }}>
              {current.user_agent}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {/* Resend is a delivery retry, distinct from classification. Only
            offered when there is actually a failure to retry. */}
        {current.delivery_status === 'failed' && (
          <Button variant="outline-primary" onClick={handleResend} disabled={sending}>
            {sending
              ? <><Spinner animation="border" size="sm" className="me-1" />Sending…</>
              : 'Resend email'}
          </Button>
        )}
        <Button
          variant={quarantined ? 'primary' : 'outline-danger'}
          onClick={handleReclassify}
          disabled={sending}
          title={quarantined
            ? 'Treat this as a real enquiry and send the notification email.'
            : 'Mark as spam. Any notification already sent cannot be recalled.'}
        >
          {sending
            ? <><Spinner animation="border" size="sm" className="me-1" />Working…</>
            : quarantined ? 'Not spam' : 'Spam'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FormSubmissionDetailModal;
